import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trophy, Calendar, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import type { DiscoveryTournament } from '@/hooks/useDiscoveryEngine';

interface Props {
  tournaments: DiscoveryTournament[];
}

export const DiscoverTournamentsWidget: React.FC<Props> = ({ tournaments }) => {
  const navigate = useNavigate();

  if (tournaments.length === 0) return null;

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center">
            <Trophy className="h-4 w-4 mr-2 text-primary" />
            Tournaments For You
          </span>
          <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={() => navigate('/tournaments')}>
            See more <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {tournaments.map((tournament) => (
          <div
            key={tournament.id}
            className="p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors"
            onClick={() => navigate(`/tournaments/${tournament.id}`)}
          >
            <div className="font-medium text-sm truncate">{tournament.name}</div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
              <Calendar className="h-3 w-3" />
              <span>{format(new Date(tournament.date), 'MMM d, yyyy')}</span>
              {tournament.groupName && <span>• {tournament.groupName}</span>}
            </div>
            <div className="flex flex-wrap gap-1 mt-1.5">
              {tournament.matchReasons.slice(0, 3).map((reason) => (
                <Badge key={reason} variant="secondary" className="text-[10px] px-1.5 py-0">
                  {reason}
                </Badge>
              ))}
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                {Math.round(tournament.score * 100)}% fit
              </Badge>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
